import {
  CustomReportFieldDto,
  CustomReportFieldType,
  CustomReportFormComponentDto,
  CustomReportFormSchemaDto
} from 'providers/api';

export const FORM_SCHEMA_VALUES_KEY = '__formioData';

const ignoredInputTypes = new Set(['button', 'submit', 'content', 'htmlelement']);

export const createEmptyCustomReportFormSchema = (): CustomReportFormSchemaDto => ({
  display: 'form',
  components: [],
});

export const normaliseCustomReportFormSchema = (schema?: CustomReportFormSchemaDto): CustomReportFormSchemaDto => {
  const components = schema?.components;

  return {
    display: schema?.display ?? 'form',
    ...(schema ?? {}),
    components: Array.isArray(components) ? components : [],
  };
};

const isCustomReportFormComponent = (value: unknown): value is CustomReportFormComponentDto => (
  Boolean(value)
  && typeof value === 'object'
  && typeof (value as CustomReportFormComponentDto).type === 'string'
);

const componentListFrom = (value: unknown): CustomReportFormComponentDto[] => (
  Array.isArray(value) ? value.filter(isCustomReportFormComponent) : []
);

const nestedComponents = (component: CustomReportFormComponentDto) => {
  const nested = componentListFrom(component.components);

  if (Array.isArray(component.columns)) {
    component.columns.forEach((column) => {
      nested.push(...componentListFrom(column.components));
    });
  }

  if (Array.isArray(component.rows)) {
    component.rows.forEach((row) => {
      if (!Array.isArray(row)) return;

      row.forEach((cell) => {
        nested.push(...componentListFrom(cell.components));
      });
    });
  }

  return nested;
};

export const collectCustomReportFormComponents = (
  components: CustomReportFormComponentDto[] = [],
): CustomReportFormComponentDto[] => componentListFrom(components).flatMap((component) => [
  component,
  ...collectCustomReportFormComponents(nestedComponents(component)),
]);

export const hasCustomReportFormInputs = (schema?: CustomReportFormSchemaDto) => (
  collectCustomReportFormComponents(schema?.components)
    .some((component) => Boolean(component.input && component.key && !ignoredInputTypes.has(component.type)))
);

const fieldTypeFromComponent = (component: CustomReportFormComponentDto) => {
  if (component.type === 'checkbox') return CustomReportFieldType.Checkbox;
  if (component.type === 'signature') return CustomReportFieldType.Signature;
  if (component.type === 'datetime' || component.type === 'day') return CustomReportFieldType.Date;
  if (component.type === 'number') return CustomReportFieldType.Number;
  if (component.type === 'select' || component.type === 'radio' || component.type === 'selectboxes') return CustomReportFieldType.Select;
  if (component.type === 'textarea') return CustomReportFieldType.MultilineText;
  return CustomReportFieldType.Text;
};

const optionsFromComponent = (component: CustomReportFormComponentDto) => {
  const values = Array.isArray(component.values)
    ? component.values
    : component.data?.values;

  if (!Array.isArray(values)) return [];

  return values
    .map((option) => option.value ?? option.label)
    .filter((option): option is string => Boolean(option));
};

export const customReportFieldsFromFormSchema = (schema?: CustomReportFormSchemaDto): CustomReportFieldDto[] => (
  collectCustomReportFormComponents(schema?.components)
    .filter((component) => Boolean(component.input && component.key && !ignoredInputTypes.has(component.type)))
    .map((component, index) => ({
      id: component.key ?? `field_${index + 1}`,
      label: component.label ?? component.key ?? `Field ${index + 1}`,
      type: fieldTypeFromComponent(component),
      required: Boolean(component.validate?.required || component.validation?.required),
      sortOrder: index,
      options: optionsFromComponent(component),
    }))
);

const decodeValue = (value?: string): unknown => {
  if (value === undefined) return undefined;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const encodeValue = (value: unknown) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

export const formDataFromCustomReportValues = (values?: Record<string, string>) => {
  const encodedData = values?.[FORM_SCHEMA_VALUES_KEY];

  if (encodedData) {
    try {
      const parsed = JSON.parse(encodedData);
      return typeof parsed === 'object' && parsed !== null ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }

  return Object.entries(values ?? {}).reduce<Record<string, unknown>>((next, [key, value]) => ({
    ...next,
    [key]: decodeValue(value),
  }), {});
};

export const customReportValuesFromFormData = (
  data: Record<string, unknown>,
  fields: CustomReportFieldDto[],
) => fields.reduce<Record<string, string>>((next, field) => {
  if (!field.id) return next;

  return {
    ...next,
    [field.id]: encodeValue(data[field.id]),
  };
}, {
  [FORM_SCHEMA_VALUES_KEY]: JSON.stringify(data),
});
